import {
  addDays,
  createDate,
  getCurrentDate,
  getDaysInMonth,
  getNthWeekdayOfMonth,
  getZonedDatetime,
  isValidTimeStrict,
  parseTime,
} from '@/shared/utils/datetime-utils';
import type { V1AutomationSchedule } from '@/generated/hooks/grimoire/models/v1AutomationSchedule';
import type { V1ScheduleConfig } from '@/generated/hooks/grimoire/models/v1ScheduleConfig';
import type { TypeTimeOfDay } from '@/generated/hooks/grimoire/models/typeTimeOfDay';
import { V1FrequencyUnit } from '@/generated/hooks/grimoire/models/v1FrequencyUnit';
import { V1DayOfWeek } from '@/generated/hooks/grimoire/models/v1DayOfWeek';
import { V1Ordinal } from '@/generated/hooks/grimoire/models/v1Ordinal';
import { V1Month } from '@/generated/hooks/grimoire/models/v1Month';
import type { IScheduleConfig } from './ScheduleTriggersDialog';
import type {
  IPresetAutofillConfig,
  IPresetLabelValues,
  IPresetOption,
  ITimezoneGroup,
  TDayOfWeek,
  TRepeatFrequency,
  TSchedulePreset,
} from './types';

/** Days of the week for the toggle group */
/** NOTE: Labels are intentionally 1 letter as per design */
export const DAYS_OF_WEEK: { value: TDayOfWeek; label: string }[] = [
  { value: 'S', label: 'S' },
  { value: 'M', label: 'M' },
  { value: 'T', label: 'T' },
  { value: 'W', label: 'W' },
  { value: 'Th', label: 'T' },
  { value: 'F', label: 'F' },
  { value: 'Sa', label: 'S' },
];

/** Empty days array constant to avoid re-creating on each render */
export const EMPTY_DAYS: TDayOfWeek[] = [];

/** All days of the week */
export const ALL_DAYS: TDayOfWeek[] = ['S', 'M', 'T', 'W', 'Th', 'F', 'Sa'];

/** Weekdays only (Monday through Friday) */
export const WEEKDAYS: TDayOfWeek[] = ['M', 'T', 'W', 'Th', 'F'];

/** Day order index map for sorting */
const DAY_ORDER: Record<TDayOfWeek, number> = {
  S: 0,
  M: 1,
  T: 2,
  W: 3,
  Th: 4,
  F: 5,
  Sa: 6,
};

/**
 * Sorts an array of days in week order (Sunday to Saturday).
 *
 * @param days - Array of days to sort
 * @returns New array sorted in week order
 */
export function sortDaysByWeekOrder(days: TDayOfWeek[]): TDayOfWeek[] {
  return [...days].sort((a, b) => DAY_ORDER[a] - DAY_ORDER[b]);
}

/** Default schedule time in 24-hour format (9:00 AM) */
export const DEFAULT_START_TIME = '09:00';
export const DEFAULT_END_TIME = '17:00';

/**
 * Returns a valid "HH:mm" time string, falling back to a default when the input
 * is missing or malformed. Prevents transient time-input values from crashing
 * the schedule preview during render (see {@link isValidTimeStrict}).
 *
 * @param time - The raw time string to validate (may be empty/partial)
 * @param fallback - Time string to use when `time` is invalid
 * @returns A valid "HH:mm" time string
 */
export function getValidTimeOrDefault(
  time: string | undefined,
  fallback: string = DEFAULT_START_TIME,
): string {
  return isValidTimeStrict(time) ? time : fallback;
}

/**
 * Creates a default schedule configuration with initial values.
 *
 * @returns Default schedule configuration
 */
export function getDefaultScheduleConfig(): IScheduleConfig {
  return {
    interval: 1,
    frequency: 'Week',
    selectedDays: [],
    repeatStartTime: DEFAULT_START_TIME,
    // TODO: Re-enable when backend supports active_window
    // repeatEndTime: DEFAULT_END_TIME,
    timezone: getDefaultTimezone(),
  };
}

/** Frequency options for the repeat interval dropdown */
export const FREQUENCY_ITEMS: { value: TRepeatFrequency; label: string }[] = [
  { value: 'Minute', label: 'Minute' },
  { value: 'Hour', label: 'Hour' },
  { value: 'Day', label: 'Day' },
  { value: 'Week', label: 'Week' },
  { value: 'Month', label: 'Month' },
  { value: 'Year', label: 'Year' },
];

/** Timezone items grouped by continent */
export const TIMEZONE_GROUPS: ITimezoneGroup[] = [
  {
    label: 'North America',
    items: [
      { value: 'America/New_York', label: 'Eastern Standard Time (EST)' },
      { value: 'America/Chicago', label: 'Central Standard Time (CST)' },
      { value: 'America/Denver', label: 'Mountain Standard Time (MST)' },
      { value: 'America/Los_Angeles', label: 'Pacific Standard Time (PST)' },
      { value: 'America/Anchorage', label: 'Alaska Standard Time (AKST)' },
      { value: 'Pacific/Honolulu', label: 'Hawaii Standard Time (HST)' },
    ],
  },
  {
    label: 'Europe & Africa',
    items: [
      { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
      { value: 'Europe/Paris', label: 'Central European Time (CET)' },
      { value: 'Europe/Helsinki', label: 'Eastern European Time (EET)' },
      { value: 'Europe/Lisbon', label: 'Western European Summer Time (WEST)' },
      { value: 'Africa/Johannesburg', label: 'Central Africa Time (CAT)' },
      { value: 'Africa/Nairobi', label: 'East Africa Time (EAT)' },
    ],
  },
  {
    label: 'Asia',
    items: [
      { value: 'Europe/Moscow', label: 'Moscow Time (MSK)' },
      { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
      { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
      { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
      { value: 'Asia/Seoul', label: 'Korea Standard Time (KST)' },
      {
        value: 'Asia/Makassar',
        label: 'Indonesia Central Standard Time (WITA)',
      },
    ],
  },
  {
    label: 'Australia & Pacific',
    items: [
      {
        value: 'Australia/Perth',
        label: 'Australian Western Standard Time (AWST)',
      },
      {
        value: 'Australia/Adelaide',
        label: 'Australian Central Standard Time (ACST)',
      },
      {
        value: 'Australia/Sydney',
        label: 'Australian Eastern Standard Time (AEST)',
      },
      { value: 'Pacific/Auckland', label: 'New Zealand Standard Time (NZST)' },
      { value: 'Pacific/Fiji', label: 'Fiji Time (FJT)' },
    ],
  },
  {
    label: 'South America',
    items: [
      {
        value: 'America/Argentina/Buenos_Aires',
        label: 'Argentina Time (ART)',
      },
      { value: 'America/La_Paz', label: 'Bolivia Time (BOT)' },
      { value: 'America/Sao_Paulo', label: 'Brasilia Time (BRT)' },
      { value: 'America/Santiago', label: 'Chile Standard Time (CLT)' },
    ],
  },
];

/**
 * Looks up the display label for a given IANA timezone ID in TIMEZONE_GROUPS.
 *
 * @param value - The IANA timezone ID (e.g. "America/New_York")
 * @returns The matching display label, or a readable version of the raw value if not found
 */
export function getTimezoneLabel(value: string): string {
  for (const group of TIMEZONE_GROUPS) {
    const match = group.items.find(item => item.value === value);
    if (match) return match.label;
  }
  return value.replace(/_/g, ' ');
}

/**
 * Returns TIMEZONE_GROUPS with `value` injected as its own group when it isn't
 * already present. The browser's detected IANA timezone (e.g. from
 * `getDefaultTimezone`) can be an alias outside the curated list (e.g.
 * "America/Buenos_Aires" instead of "America/Argentina/Buenos_Aires"), which
 * would otherwise leave the Select's value blank.
 *
 * @param value - The currently selected IANA timezone ID
 * @param fallbackGroupLabel - Translated label for the injected group
 * @returns TIMEZONE_GROUPS, with a fallback group prepended if needed
 */
export function getTimezoneGroupsWithFallback(
  value: string,
  fallbackGroupLabel: string,
): ITimezoneGroup[] {
  const exists = TIMEZONE_GROUPS.some(group =>
    group.items.some(item => item.value === value),
  );
  if (exists) return TIMEZONE_GROUPS;

  return [
    {
      label: fallbackGroupLabel,
      items: [{ value, label: getTimezoneLabel(value) }],
    },
    ...TIMEZONE_GROUPS,
  ];
}

/**
 * Autofill configurations for each preset.
 * When a preset is selected, these values are used to populate the form fields.
 */
export const PRESET_AUTOFILL: Record<TSchedulePreset, IPresetAutofillConfig> = {
  'does-not-repeat': {
    interval: 0,
    frequency: undefined,
    days: [],
  },
  'every-5-mins': {
    interval: 5,
    frequency: 'Minute',
    days: ALL_DAYS,
  },
  hourly: {
    interval: 1,
    frequency: 'Hour', // Hours not supported in frequency dropdown
    days: ALL_DAYS,
  },
  daily: {
    interval: 1,
    frequency: 'Day',
    days: ALL_DAYS,
    endTime: '',
  },
  'every-weekday-or-weekend': {
    interval: 1,
    frequency: 'Week',
    days: WEEKDAYS,
    endTime: '',
  },
  'weekly-on-day': {
    interval: 1,
    frequency: 'Week',
    endTime: '',
    // days will be set dynamically based on current day
  },
  'monthly-on-nth-day': {
    interval: 1,
    frequency: 'Month',
    endTime: '',
    // days will be set dynamically based on current day
  },
  'monthly-on-last-day': {
    interval: 1,
    frequency: 'Month',
    endTime: '',
    // days will be set dynamically based on current day
  },
  'annually-on-month-day': {
    interval: 1,
    frequency: 'Year',
    days: [],
    endTime: '',
  },
};

/** Individual preset options */

/* TODO: Uncomment when BE supports no-repeat */
// const PRESET_NO_REPEAT: IPresetOption = {
//   value: 'does-not-repeat',
//   labelKey: 'automation.schedule.repeats.preset.doesNotRepeat',
// };

const PRESET_EVERY_5_MINS: IPresetOption = {
  value: 'every-5-mins',
  labelKey: 'automation.schedule.repeats.preset.every5Mins',
};

const PRESET_HOURLY: IPresetOption = {
  value: 'hourly',
  labelKey: 'automation.schedule.repeats.preset.hourly',
};

const PRESET_DAILY: IPresetOption = {
  value: 'daily',
  labelKey: 'automation.schedule.repeats.preset.daily',
};

const PRESET_EVERY_WEEKDAY: IPresetOption = {
  value: 'every-weekday-or-weekend',
  labelKey: 'automation.schedule.repeats.preset.everyWeekday',
};

const PRESET_WEEKLY_ON_DAY: IPresetOption = {
  value: 'weekly-on-day',
  labelKey: 'automation.schedule.repeats.preset.weeklyOnDay',
};

const PRESET_MONTHLY_ON_NTH_DAY: IPresetOption = {
  value: 'monthly-on-nth-day',
  labelKey: 'automation.schedule.repeats.preset.monthlyOnNthDay',
};

const PRESET_MONTHLY_ON_LAST_DAY: IPresetOption = {
  value: 'monthly-on-last-day',
  labelKey: 'automation.schedule.repeats.preset.monthlyOnLastDay',
};

const PRESET_ANNUALLY_ON_MONTH_DAY: IPresetOption = {
  value: 'annually-on-month-day',
  labelKey: 'automation.schedule.repeats.preset.annuallyOnMonthDay',
};

/**
 * Returns the preset options grouped by rows based on conditions.
 * When showAllPresets is true (last week of month), shows 4 rows with monthly-on-last-day.
 * When false, shows 3 rows without monthly-on-last-day.
 *
 * @param showAllPresets - Whether to show all presets (4 rows) or compact mode (3 rows)
 * @returns Array of preset option rows
 */
export function getPresetOptionRows(
  showAllPresets: boolean = true,
): IPresetOption[][] {
  const row1 = [
    // PRESET_NO_REPEAT,
    PRESET_EVERY_5_MINS,
    PRESET_HOURLY,
    PRESET_DAILY,
  ];
  const row2 = [PRESET_EVERY_WEEKDAY, PRESET_WEEKLY_ON_DAY];

  if (showAllPresets) {
    const row3 = [PRESET_MONTHLY_ON_NTH_DAY, PRESET_MONTHLY_ON_LAST_DAY];
    const row4 = [PRESET_ANNUALLY_ON_MONTH_DAY];
    return [row1, row2, row3, row4];
  }

  // Compact mode: hide monthly-on-last-day, put annually in row 3
  const row3 = [PRESET_MONTHLY_ON_NTH_DAY, PRESET_ANNUALLY_ON_MONTH_DAY];
  return [row1, row2, row3];
}

/**
 * Formats a scheduled run's time display string.
 * Combines day of week, start time, optional end time, and timezone.
 *
 * @param dayOfWeek - Day of the week abbreviation (e.g., 'Mon', 'Tue')
 * @param startTimeDisplay - Formatted start time (e.g., '9:00 AM')
 * @param endTimeDisplay - Optional formatted end time (e.g., '5:00 PM')
 * @param timezone - Timezone abbreviation (e.g., 'PT', 'ET')
 * @returns Formatted time display string
 *
 * @example
 * formatScheduleTimeDisplay('Mon', '9:00 AM', '5:00 PM', 'PT')
 * // Returns: 'Mon, 9:00 AM - 5:00 PM PT'
 *
 * @example
 * formatScheduleTimeDisplay('Mon', '9:00 AM', null, 'PT')
 * // Returns: 'Mon, 9:00 AM PT'
 */
export function formatScheduleTimeDisplay(
  dayOfWeek: string,
  startTimeDisplay: string,
  endTimeDisplay: string | null,
  timezone: string,
): string {
  if (endTimeDisplay) {
    return `${dayOfWeek}, ${startTimeDisplay} - ${endTimeDisplay} ${timezone}`;
  }
  return `${dayOfWeek}, ${startTimeDisplay} ${timezone}`;
}

/**
 * Gets the user's local timezone.
 * Falls back to 'America/Los_Angeles' if unable to detect.
 *
 * @returns IANA timezone ID string
 */
export const getDefaultTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/Los_Angeles';
  }
};

/**
 * Converts a week number to its ordinal word (first, second, third, fourth, last).
 *
 * @param n - The week number (1-5)
 * @param isLast - Whether this is the last occurrence of the weekday in the month
 * @returns The ordinal word string
 */
export function getOrdinalWord(n: number, isLast: boolean = false): string {
  if (isLast) {
    return 'last';
  }
  const words = ['first', 'second', 'third', 'fourth', 'fifth'];
  return words[n - 1] || 'first';
}

/**
 * Checks if the given date is the last occurrence of its weekday in the month.
 *
 * @param date - The date to check
 * @returns True if this is the last occurrence of the weekday in the month
 */
function isLastWeekdayOfMonth(date: Date): boolean {
  const dayOfMonth = date.getDate();
  const lastDayOfMonth = getDaysInMonth(date);

  // If adding 7 days would exceed the month, this is the last occurrence
  return dayOfMonth + 7 > lastDayOfMonth;
}

/**
 * Computes preset label values from a given date.
 *
 * @param date - The date to compute label values from
 * @returns Object containing computed label values for preset interpolation
 */
export function computePresetLabelValues(date: Date): IPresetLabelValues {
  const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
  const monthName = date.toLocaleDateString('en-US', { month: 'long' });
  const dayOfMonth = date.getDate();
  const weekOfMonth = Math.ceil(dayOfMonth / 7);
  const isLast = isLastWeekdayOfMonth(date);

  return {
    day: dayOfWeek,
    number: getOrdinalWord(weekOfMonth, isLast),
    date: `${monthName} ${dayOfMonth}`,
  };
}

/**
 * Converts a Date object to its corresponding TDayOfWeek value.
 *
 * @param date - The date to get the day of week from
 * @returns The TDayOfWeek value for the given date
 */
export function getDayOfWeekFromDate(date: Date): TDayOfWeek {
  const dayIndex = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const dayMap: TDayOfWeek[] = ['S', 'M', 'T', 'W', 'Th', 'F', 'Sa'];
  return dayMap[dayIndex];
}

/** Base period of days to calculate scheduled runs for dense schedules */
export const SCHEDULE_PERIOD_DAYS = 36;

/**
 * Minimum upcoming occurrences to aim for in a preview batch.
 * Matches the initial list display count in SchedulePreview.
 */
const MIN_PREVIEW_OCCURRENCES = 5;

/**
 * Days to scan when generating a preview batch.
 *
 * Weekly (and monthly) intervals can push the next fire beyond
 * {@link SCHEDULE_PERIOD_DAYS} — e.g. every 6 weeks is ~42 days when the
 * current week is off-cadence — which previously blanked the preview while
 * Save stayed enabled (QA-647). Expand the horizon enough to cover at least
 * {@link MIN_PREVIEW_OCCURRENCES} matches.
 */
export function getPreviewPeriodDays(config: IScheduleConfig): number {
  const interval = Math.max(1, config.interval ?? 1);

  switch (config.frequency) {
    case 'Week':
      // First match can be up to `interval` weeks out; +7 covers a partial week.
      return Math.max(
        SCHEDULE_PERIOD_DAYS,
        interval * 7 * MIN_PREVIEW_OCCURRENCES + 7,
      );
    case 'Month':
      return Math.max(
        SCHEDULE_PERIOD_DAYS,
        interval * 31 * MIN_PREVIEW_OCCURRENCES + 31,
      );
    default:
      return SCHEDULE_PERIOD_DAYS;
  }
}

/** Map of IANA timezone IDs to short abbreviations for display */
export const TIMEZONE_SHORT_ABBR: Record<string, string> = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
  'America/Anchorage': 'AKT',
  'Pacific/Honolulu': 'HT',
  'Europe/London': 'GMT',
  'Europe/Paris': 'CET',
  'Europe/Helsinki': 'EET',
  'Europe/Lisbon': 'WET',
  'Africa/Johannesburg': 'CAT',
  'Africa/Nairobi': 'EAT',
  'Europe/Moscow': 'MSK',
  'Asia/Kolkata': 'IST',
  'Asia/Shanghai': 'CST',
  'Asia/Tokyo': 'JST',
  'Asia/Seoul': 'KST',
  'Asia/Makassar': 'WITA',
  'Australia/Perth': 'AWT',
  'Australia/Adelaide': 'ACT',
  'Australia/Sydney': 'AET',
  'Pacific/Auckland': 'NZT',
  'Pacific/Fiji': 'FJT',
  'America/Argentina/Buenos_Aires': 'ART',
  'America/La_Paz': 'BOT',
  'America/Sao_Paulo': 'BRT',
  'America/Santiago': 'CLT',
};

/** Map TDayOfWeek to JS Date.getDay() index (0=Sunday, 1=Monday, etc.) */
const DAY_TO_INDEX: Record<TDayOfWeek, number> = {
  S: 0,
  M: 1,
  T: 2,
  W: 3,
  Th: 4,
  F: 5,
  Sa: 6,
};

/**
 * Gets the start of the week (Sunday) for a given date.
 */
function getStartOfWeek(date: Date): Date {
  const d = createDate(date.getFullYear(), date.getMonth(), date.getDate());
  const dayOfWeek = d.getDay(); // 0 = Sunday
  return addDays(d, -dayOfWeek);
}

/**
 * Calculates the number of calendar weeks between two dates.
 * Uses Sunday as the start of the week.
 * Used to determine if a date falls on an interval week.
 */
function getWeeksBetween(startDate: Date, targetDate: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  const startWeekBegin = getStartOfWeek(startDate).getTime();
  const targetWeekBegin = getStartOfWeek(targetDate).getTime();
  const daysDiff = Math.round((targetWeekBegin - startWeekBegin) / msPerDay);
  return Math.floor(daysDiff / 7);
}

/**
 * Calculates the number of months between two dates.
 */
function getMonthsBetween(startDate: Date, targetDate: Date): number {
  return (
    (targetDate.getFullYear() - startDate.getFullYear()) * 12 +
    (targetDate.getMonth() - startDate.getMonth())
  );
}

/**
 * Generates scheduled dates for a given period starting from a specific date.
 */
function getScheduledDatesForPeriod(
  config: IScheduleConfig,
  periodStart: Date,
  periodDays: number,
): Date[] {
  const {
    frequency,
    selectedDays: configSelectedDays,
    startDate,
    interval = 1,
  } = config;
  const selectedDays = frequency === 'Day' ? ALL_DAYS : configSelectedDays;
  const dates: Date[] = [];
  const configStart = startDate ?? getCurrentDate();
  const endDate = addDays(periodStart, periodDays);

  // Normalize periodStart to start of day for date comparisons
  const periodStartDay = createDate(
    periodStart.getFullYear(),
    periodStart.getMonth(),
    periodStart.getDate(),
  );

  switch (frequency) {
    case 'Month': {
      const { nthWeekday } = config;

      // Calculate how many months from configStart to periodStart, then round up to next interval
      const monthsFromStart = getMonthsBetween(configStart, periodStartDay);
      const intervalsToSkip = Math.max(
        0,
        Math.ceil(monthsFromStart / interval),
      );
      const startMonth = intervalsToSkip * interval;

      // Start from the first month on the interval that is >= periodStart
      const current = createDate(
        configStart.getFullYear(),
        configStart.getMonth() + startMonth,
        1,
      );

      // If nthWeekday is set, find the nth occurrence of the weekday
      if (nthWeekday !== undefined && selectedDays && selectedDays.length > 0) {
        const targetDayIndex = DAY_TO_INDEX[selectedDays[0]];

        while (current < endDate) {
          const scheduledDate = getNthWeekdayOfMonth(
            current.getFullYear(),
            current.getMonth(),
            targetDayIndex,
            nthWeekday,
          );

          if (
            scheduledDate &&
            scheduledDate >= periodStartDay &&
            scheduledDate < endDate
          ) {
            dates.push(scheduledDate);
          }

          current.setMonth(current.getMonth() + interval);
        }
      } else {
        // Use day of month from start date
        const targetDayOfMonth = configStart.getDate();

        while (current < endDate) {
          const daysInMonth = getDaysInMonth(current);
          const actualDay = Math.min(targetDayOfMonth, daysInMonth);
          current.setDate(actualDay);

          if (current >= periodStartDay && current < endDate) {
            dates.push(addDays(current, 0));
          }

          current.setMonth(current.getMonth() + interval);
          current.setDate(1);
        }
      }
      break;
    }

    case 'Year': {
      const targetMonth = configStart.getMonth();
      const targetDay = configStart.getDate();

      // Normalize to start of day so today's date is included
      const periodStartDay = createDate(
        periodStart.getFullYear(),
        periodStart.getMonth(),
        periodStart.getDate(),
      );

      // Show next 5 occurrences for yearly schedules (accounting for interval)
      let occurrencesFound = 0;
      let yearOffset = 0;
      const maxYearsToCheck = 50; // Safety limit

      while (occurrencesFound < 5 && yearOffset < maxYearsToCheck) {
        const candidateYear = periodStart.getFullYear() + yearOffset;
        const candidateDate = createDate(candidateYear, targetMonth, targetDay);

        if (candidateDate >= periodStartDay) {
          // Check if this year matches the interval pattern
          const yearsDiff = candidateYear - configStart.getFullYear();
          if (yearsDiff >= 0 && yearsDiff % interval === 0) {
            dates.push(candidateDate);
            occurrencesFound++;
          }
        }
        yearOffset++;
      }
      break;
    }

    case 'Week': {
      // Weekly: use selected days of the week, respecting interval
      const selectedDayIndices = selectedDays?.map(d => DAY_TO_INDEX[d]) ?? [];

      if (selectedDayIndices.length === 0) {
        return [];
      }

      for (let i = 0; i < periodDays; i++) {
        const date = addDays(periodStart, i);
        if (selectedDayIndices.includes(date.getDay())) {
          // Check if this week matches the interval pattern
          const weeksDiff = getWeeksBetween(configStart, date);
          if (weeksDiff >= 0 && weeksDiff % interval === 0) {
            dates.push(date);
          }
        }
      }
      break;
    }

    default: {
      // Daily and other frequencies: use selected days of the week
      const selectedDayIndices = selectedDays?.map(d => DAY_TO_INDEX[d]) ?? [];

      if (selectedDayIndices.length === 0) {
        return [];
      }

      for (let i = 0; i < periodDays; i++) {
        const date = addDays(periodStart, i);
        if (selectedDayIndices.includes(date.getDay())) {
          dates.push(date);
        }
      }
      break;
    }
  }

  return dates;
}

/**
 * Generates initial batch of scheduled dates within the preview horizon.
 */
export function getScheduledDates(config: IScheduleConfig): Date[] {
  const currentDate = getCurrentDate();
  const configStartDate = config.startDate ?? currentDate;
  const start = configStartDate > currentDate ? configStartDate : currentDate;
  const dates = getScheduledDatesForPeriod(
    config,
    start,
    getPreviewPeriodDays(config),
  );

  // Filter out occurrences whose zoned fire time has already passed
  const tz = config.timezone ?? getDefaultTimezone();
  const time = getValidTimeOrDefault(config.repeatStartTime);
  const nowMs = Date.now();
  return dates.filter(d => getZonedDatetime(d, time, tz).getTime() > nowMs);
}

/**
 * Checks if the schedule config is complete enough to show a preview and
 * enable Save. Weekly schedules need at least one day; Begin time must be a
 * real clock value — empty previously left Save enabled (QA-618). Save also
 * stays disabled when the preview would collapse to the blank placeholder
 * (no upcoming dates) (QA-647).
 *
 * @param config - The schedule configuration to validate
 * @returns True if the config is valid for preview and saving
 */
export function isConfigValidForPreview(config: IScheduleConfig): boolean {
  if (!config.frequency) return false;

  // Weekly schedules require at least one day selected
  if (config.frequency === 'Week' && (config.selectedDays?.length ?? 0) === 0) {
    return false;
  }

  // Do not coerce empty/partial times to the default for Save. Preview still
  // uses getValidTimeOrDefault so a transient input cannot crash render.
  if (!isValidTimeStrict(config.repeatStartTime)) {
    return false;
  }

  // Gate Save on a visible preview — empty dates previously left Save enabled
  // while the UI showed only the blank placeholder (QA-647).
  return getScheduledDates(config).length > 0;
}

/**
 * Generates the next batch of scheduled dates for pagination.
 */
export function addMoreScheduledDates(
  config: IScheduleConfig,
  existingDates: Date[],
): Date[] {
  if (existingDates.length === 0) {
    return getScheduledDates(config);
  }

  const lastDate = existingDates[existingDates.length - 1];
  const nextPeriodStart = addDays(lastDate, 1);

  return getScheduledDatesForPeriod(
    config,
    nextPeriodStart,
    getPreviewPeriodDays(config),
  );
}

// ─── Proto Mapping Utilities ─────────────────────────────────────────────────

/** Maps UI frequency strings to proto V1FrequencyUnit enum values */
const FREQUENCY_TO_UNIT_MAP: Record<TRepeatFrequency, V1FrequencyUnit> = {
  Minute: V1FrequencyUnit.FREQUENCY_UNIT_MINUTE,
  Hour: V1FrequencyUnit.FREQUENCY_UNIT_HOUR,
  Day: V1FrequencyUnit.FREQUENCY_UNIT_DAY,
  Week: V1FrequencyUnit.FREQUENCY_UNIT_WEEK,
  Month: V1FrequencyUnit.FREQUENCY_UNIT_MONTH,
  Year: V1FrequencyUnit.FREQUENCY_UNIT_YEAR,
};

/** Maps proto V1FrequencyUnit enum values back to UI frequency strings */
const UNIT_TO_FREQUENCY_MAP: Partial<
  Record<V1FrequencyUnit, TRepeatFrequency>
> = {
  [V1FrequencyUnit.FREQUENCY_UNIT_MINUTE]: 'Minute',
  [V1FrequencyUnit.FREQUENCY_UNIT_HOUR]: 'Hour',
  [V1FrequencyUnit.FREQUENCY_UNIT_DAY]: 'Day',
  [V1FrequencyUnit.FREQUENCY_UNIT_WEEK]: 'Week',
  [V1FrequencyUnit.FREQUENCY_UNIT_MONTH]: 'Month',
  [V1FrequencyUnit.FREQUENCY_UNIT_YEAR]: 'Year',
};

/** Maps UI day-of-week abbreviations to proto V1DayOfWeek enum values */
export const DAY_TO_PROTO_MAP: Record<TDayOfWeek, V1DayOfWeek> = {
  S: V1DayOfWeek.DAY_OF_WEEK_SUNDAY,
  M: V1DayOfWeek.DAY_OF_WEEK_MONDAY,
  T: V1DayOfWeek.DAY_OF_WEEK_TUESDAY,
  W: V1DayOfWeek.DAY_OF_WEEK_WEDNESDAY,
  Th: V1DayOfWeek.DAY_OF_WEEK_THURSDAY,
  F: V1DayOfWeek.DAY_OF_WEEK_FRIDAY,
  Sa: V1DayOfWeek.DAY_OF_WEEK_SATURDAY,
};

/** Maps proto V1DayOfWeek enum values back to UI day-of-week abbreviations */
const PROTO_TO_DAY_MAP: Partial<Record<V1DayOfWeek, TDayOfWeek>> = {
  [V1DayOfWeek.DAY_OF_WEEK_SUNDAY]: 'S',
  [V1DayOfWeek.DAY_OF_WEEK_MONDAY]: 'M',
  [V1DayOfWeek.DAY_OF_WEEK_TUESDAY]: 'T',
  [V1DayOfWeek.DAY_OF_WEEK_WEDNESDAY]: 'W',
  [V1DayOfWeek.DAY_OF_WEEK_THURSDAY]: 'Th',
  [V1DayOfWeek.DAY_OF_WEEK_FRIDAY]: 'F',
  [V1DayOfWeek.DAY_OF_WEEK_SATURDAY]: 'Sa',
};

/** Maps JS 0-indexed month number to proto V1Month enum values */
const MONTH_INDEX_TO_PROTO: V1Month[] = [
  V1Month.MONTH_JANUARY,
  V1Month.MONTH_FEBRUARY,
  V1Month.MONTH_MARCH,
  V1Month.MONTH_APRIL,
  V1Month.MONTH_MAY,
  V1Month.MONTH_JUNE,
  V1Month.MONTH_JULY,
  V1Month.MONTH_AUGUST,
  V1Month.MONTH_SEPTEMBER,
  V1Month.MONTH_OCTOBER,
  V1Month.MONTH_NOVEMBER,
  V1Month.MONTH_DECEMBER,
];

/** Maps proto V1Month enum values back to JS 0-indexed month number */
const PROTO_TO_MONTH_INDEX: Partial<Record<V1Month, number>> = {
  [V1Month.MONTH_JANUARY]: 0,
  [V1Month.MONTH_FEBRUARY]: 1,
  [V1Month.MONTH_MARCH]: 2,
  [V1Month.MONTH_APRIL]: 3,
  [V1Month.MONTH_MAY]: 4,
  [V1Month.MONTH_JUNE]: 5,
  [V1Month.MONTH_JULY]: 6,
  [V1Month.MONTH_AUGUST]: 7,
  [V1Month.MONTH_SEPTEMBER]: 8,
  [V1Month.MONTH_OCTOBER]: 9,
  [V1Month.MONTH_NOVEMBER]: 10,
  [V1Month.MONTH_DECEMBER]: 11,
};

/** Maps nthWeekday number to proto V1Ordinal enum values */
function nthWeekdayToOrdinal(n: number): V1Ordinal {
  if (n === -1) return V1Ordinal.ORDINAL_LAST;
  const map: Record<number, V1Ordinal> = {
    1: V1Ordinal.ORDINAL_FIRST,
    2: V1Ordinal.ORDINAL_SECOND,
    3: V1Ordinal.ORDINAL_THIRD,
    4: V1Ordinal.ORDINAL_FOURTH,
    5: V1Ordinal.ORDINAL_FIFTH,
  };
  return map[n] ?? V1Ordinal.ORDINAL_FIRST;
}

/** Maps proto V1Ordinal enum values back to nthWeekday number */
function ordinalToNthWeekday(ordinal: V1Ordinal): number {
  const map: Partial<Record<V1Ordinal, number>> = {
    [V1Ordinal.ORDINAL_FIRST]: 1,
    [V1Ordinal.ORDINAL_SECOND]: 2,
    [V1Ordinal.ORDINAL_THIRD]: 3,
    [V1Ordinal.ORDINAL_FOURTH]: 4,
    [V1Ordinal.ORDINAL_FIFTH]: 5,
    [V1Ordinal.ORDINAL_LAST]: -1,
  };
  return map[ordinal] ?? 1;
}

/** Converts a TypeTimeOfDay to an "HH:mm" string */
export function timeOfDayToString(time: TypeTimeOfDay): string {
  const hours = String(time.hours ?? 0).padStart(2, '0');
  const minutes = String(time.minutes ?? 0).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Converts the UI schedule config state into the proto V1ScheduleConfig
 * used by the create/update schedule API endpoints.
 */
export function buildScheduleConfig(config: IScheduleConfig): V1ScheduleConfig {
  const referenceDate = config.startDate ?? getCurrentDate();

  const scheduleConfig: V1ScheduleConfig = {
    start: {
      start_date: config.startDate
        ? {
            year: config.startDate.getFullYear(),
            month: config.startDate.getMonth() + 1,
            day: config.startDate.getDate(),
          }
        : undefined,
      time: parseTime(getValidTimeOrDefault(config.repeatStartTime)),
      time_zone: config.timezone ?? getDefaultTimezone(),
    },
  };

  // repeat_interval
  if (config.frequency) {
    scheduleConfig.repeat_interval = {
      unit: FREQUENCY_TO_UNIT_MAP[config.frequency],
      interval: config.interval ?? 1,
    };
  }

  // weekly_pattern — for Minute, Hour, and Week frequencies with selected days
  // The UI shows day toggles for these frequencies so users can restrict which days the schedule runs
  if (
    (config.frequency === 'Minute' ||
      config.frequency === 'Hour' ||
      config.frequency === 'Week') &&
    config.selectedDays &&
    config.selectedDays.length > 0
  ) {
    scheduleConfig.weekly_pattern = {
      days: config.selectedDays.map(day => DAY_TO_PROTO_MAP[day]),
    };
  }

  // monthly_pattern — only for Month frequency
  if (config.frequency === 'Month') {
    if (
      config.nthWeekday !== undefined &&
      config.selectedDays &&
      config.selectedDays.length > 0
    ) {
      // Ordinal weekday pattern (e.g., "2nd Monday")
      scheduleConfig.monthly_pattern = {
        ordinal_day: {
          ordinal: nthWeekdayToOrdinal(config.nthWeekday),
          day: DAY_TO_PROTO_MAP[config.selectedDays[0]],
        },
      };
    } else {
      // Specific day of month
      scheduleConfig.monthly_pattern = {
        day_number: referenceDate.getDate(),
      };
    }
  }

  // yearly_pattern — only for Year frequency
  if (config.frequency === 'Year') {
    scheduleConfig.yearly_pattern = {
      month:
        MONTH_INDEX_TO_PROTO[referenceDate.getMonth()] ?? V1Month.MONTH_JANUARY,
      day_pattern: {
        day_number: referenceDate.getDate(),
      },
    };
  }

  // TODO: Re-enable when backend supports active_window runtime enforcement
  // Backend edge case: active_window exists in proto but is not checked in scheduler's runtime filter
  // active_window — only for Minute/Hour frequencies with both start and end times
  // if (
  //   (config.frequency === 'Minute' || config.frequency === 'Hour') &&
  //   config.repeatStartTime &&
  //   config.repeatEndTime
  // ) {
  //   scheduleConfig.active_window = {
  //     window_start: parseTime(config.repeatStartTime),
  //     window_end: parseTime(config.repeatEndTime),
  //   };
  // }

  return scheduleConfig;
}

/**
 * Finds the most recent date, on or before today, that falls on `dayNumber`.
 * Months too short to contain the day are skipped so it is never clamped
 * (e.g. the 31st stays the 31st). A past date is used because a future
 * start date would hold back runs until then.
 *
 * @param dayNumber - Day of the month (1-31)
 * @returns The most recent matching date
 */
function getMostRecentDayOfMonth(dayNumber: number): Date {
  const now = getCurrentDate();
  for (let monthsBack = 0; monthsBack < 12; monthsBack++) {
    const monthStart = createDate(
      now.getFullYear(),
      now.getMonth() - monthsBack,
      1,
    );
    if (getDaysInMonth(monthStart) < dayNumber) continue;
    const candidate = createDate(
      monthStart.getFullYear(),
      monthStart.getMonth(),
      dayNumber,
    );
    if (candidate <= now) return candidate;
  }
  return createDate(now.getFullYear(), now.getMonth(), 1);
}

/**
 * Converts a proto V1AutomationSchedule into the UI IScheduleConfig state
 * for populating the form when editing an existing schedule.
 */
export function parseScheduleConfig(
  schedule: V1AutomationSchedule,
): IScheduleConfig {
  const { config } = schedule;

  // Defensive check: return default config if config or start is missing
  if (!config || !config.start) {
    return getDefaultScheduleConfig();
  }

  const {
    start,
    repeat_interval,
    weekly_pattern,
    monthly_pattern,
    yearly_pattern,
    // TODO: Re-enable when backend supports active_window
    // active_window,
  } = config;

  // Reverse-map frequency
  const frequency = repeat_interval?.unit
    ? UNIT_TO_FREQUENCY_MAP[repeat_interval.unit]
    : undefined;

  // Reverse-map days from weekly_pattern
  let selectedDays: TDayOfWeek[] = [];
  if (weekly_pattern?.days) {
    selectedDays = weekly_pattern.days
      .map(d => PROTO_TO_DAY_MAP[d])
      .filter((d): d is TDayOfWeek => d !== undefined);
  }

  // Reverse-map nthWeekday from monthly_pattern
  let nthWeekday: number | undefined;
  if (monthly_pattern?.ordinal_day) {
    nthWeekday = ordinalToNthWeekday(monthly_pattern.ordinal_day.ordinal);
    const day = PROTO_TO_DAY_MAP[monthly_pattern.ordinal_day.day];
    if (day) selectedDays = [day];
  }

  // Build startDate from start.start_date
  let startDate: Date | undefined;
  if (
    start.start_date?.year &&
    start.start_date.month &&
    start.start_date.day
  ) {
    startDate = createDate(
      start.start_date.year,
      start.start_date.month - 1,
      start.start_date.day,
    );
  }

  // For yearly, also build startDate from yearly_pattern if not already set
  if (!startDate && yearly_pattern) {
    const monthIndex = PROTO_TO_MONTH_INDEX[yearly_pattern.month] ?? 0;
    const dayNumber = yearly_pattern.day_pattern?.day_number ?? 1;
    startDate = createDate(
      getCurrentDate().getFullYear(),
      monthIndex,
      dayNumber,
    );
  }

  // Without this, re-saving falls back to today's date and silently moves the monthly day
  if (!startDate && monthly_pattern?.day_number) {
    startDate = getMostRecentDayOfMonth(monthly_pattern.day_number);
  }

  // Build times
  const repeatStartTime = start.time
    ? timeOfDayToString(start.time)
    : DEFAULT_START_TIME;

  // TODO: Re-enable when backend supports active_window runtime enforcement
  // const repeatEndTime = active_window?.window_end
  //   ? timeOfDayToString(active_window.window_end)
  //   : undefined;

  return {
    startDate,
    timezone: start.time_zone ?? getDefaultTimezone(),
    interval: repeat_interval?.interval ?? 1,
    frequency,
    selectedDays,
    nthWeekday,
    repeatStartTime,
    // TODO: Re-enable when backend supports active_window
    // repeatEndTime,
  };
}
